/**
 * Offline support — replaces utils/offlineQueue.ts
 *
 * Uses DrySW's client-side API for background sync queue management.
 * The SW handles request replay via Background Sync API with cross-browser
 * fallback (visibility-triggered replay on Safari/Firefox).
 *
 * The old offlineQueue.ts used raw IndexedDB + Background Sync directly.
 * Now DrySW handles all of that through its message bridge protocol.
 */

import { createDrySWClient } from '@drystuff/sw/client';
import type { DrySWClient } from '@drystuff/sw/client';
import { config } from './config';

let swClient: DrySWClient | null = null;
let visibilityCleanup: (() => void) | null = null;

/**
 * Push the current auth token into the service worker's token store so queued
 * requests replay AUTHENTICATED.
 *
 * The SW token store is separate from the app, and DryAuth's in-memory tokens
 * are not hydrated on a fresh page load (same root cause as the WebSocket ticket
 * bug) — so the SW's own auth-sync subscribe fires with null and clears the
 * token, and offline replays then 401 and the queued submission is dropped.
 * We source the token from localStorage (the source of truth) explicitly.
 *
 * Call this on startup and right before queueing an offline request.
 */
export function syncSWAuthToken(): void {
  if (!swClient) return;
  try {
    const accessToken = localStorage.getItem(config.auth.storageKey);
    if (accessToken) {
      swClient.setTokens({ accessToken });
    } else {
      swClient.clearTokens();
    }
  } catch {
    /* SW unavailable — best effort */
  }
}

/**
 * Initialize the DrySW client. Call once from DryContext.
 * Must be called after SW registration completes.
 */
export function initOfflineSupport(): DrySWClient | null {
  try {
    swClient = createDrySWClient();

    // Install visibility-triggered queue replay (cross-browser fallback for
    // Safari/Firefox where Background Sync doesn't exist)
    visibilityCleanup = swClient.installVisibilitySync();

    // Also flush queue when coming back online
    const onlineHandler = () => swClient?.flushQueue();
    window.addEventListener('online', onlineHandler);

    return swClient;
  } catch {
    // Service worker not available
    console.warn('[Offline] Service worker not available — offline support disabled');
    return null;
  }
}

/**
 * Get the DrySW client instance.
 */
export function getOfflineClient(): DrySWClient | null {
  return swClient;
}

/**
 * Dispose offline support listeners.
 */
export function disposeOfflineSupport(): void {
  visibilityCleanup?.();
  visibilityCleanup = null;
  swClient = null;
}

/**
 * Check if app is currently offline.
 */
export function isOffline(): boolean {
  return !navigator.onLine;
}

// NOTE: submitWithOfflineSupport (the DrySW request-replay submit path) was
// retired when form capture moved onto the DryStore formSubmissions collection
// + replication (see dry/useCapture.ts, dry/capture-replication.ts). The DrySW
// token-sync, push, and queue-introspection helpers below remain in use.

/**
 * Get the queue status from the SW.
 * Returns the number of queued requests.
 */
export async function getQueuedCount(): Promise<number> {
  if (!swClient) return 0;
  try {
    const status = await swClient.getStatus();
    return status.queueSize;
  } catch {
    return 0;
  }
}

/**
 * Manually trigger queue flush.
 */
export function flushQueue(): void {
  swClient?.flushQueue();
}

/**
 * Clear all queued requests.
 */
export function clearQueue(): void {
  swClient?.clearQueue();
}

/**
 * Listen for queue replay events from the SW.
 * Returns a cleanup function.
 */
export function onQueueReplayed(
  handler: (id: string, success: boolean) => void,
): () => void {
  if (!swClient) return () => {};
  try {
    return swClient.onMessage((msg: any) => {
      if (msg.type === 'QUEUE_REPLAYED') {
        handler(msg.payload.id, msg.payload.success);
      }
    });
  } catch {
    // Service worker not available (e.g. no SW registered, HTTP context)
    return () => {};
  }
}
